import { useEffect, useRef, useState } from 'react';
import { AlertCircle, GitMerge, X } from 'lucide-react';
import SearchableSelect, { type SearchableSelectOption } from './SearchableSelect';
import { getCustomers, mergeCustomers } from '../utils/customerApi';
import { ApiValidationError } from '../types/auth';
import type { Customer } from '../types/customer';
import { formatCurrency } from '../utils/formatCurrency';

export interface CustomerMergeModalProps {
  duplicate: Customer;
  onClose: () => void;
  onMerged: (keeper: Customer) => void;
}

const SEARCH_DEBOUNCE_MS = 300;

const customerToOption = (customer: Customer): SearchableSelectOption => ({
  value: customer.id,
  label: customer.display_name,
  description: [customer.email, customer.phone].filter(Boolean).join(' · ') || undefined,
});

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const balanceOf = (customer: Customer) => parseFloat(customer.balance) || 0;
const creditOf = (customer: Customer) => parseFloat(customer.credit_balance ?? '0') || 0;

const CustomerMergeModal = ({ duplicate, onClose, onMerged }: CustomerMergeModalProps) => {
  const [keeperSearchInput, setKeeperSearchInput] = useState('');
  const [keeperOptions, setKeeperOptions] = useState<SearchableSelectOption[]>([]);
  const [keeperSearchLoading, setKeeperSearchLoading] = useState(false);
  const [selectedKeeperId, setSelectedKeeperId] = useState<number>(0);
  const [selectedKeeper, setSelectedKeeper] = useState<Customer | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[] | undefined>>({});

  const keeperLookupRef = useRef<Map<number, Customer>>(new Map());

  const fieldError = (field: string) => fieldErrors[field]?.[0];

  useEffect(() => {
    const timer = setTimeout(async () => {
      setKeeperSearchLoading(true);
      try {
        const result = await getCustomers({
          search: keeperSearchInput.trim() || undefined,
          active: true,
          per_page: 25,
          page: 1,
        });
        for (const customer of result.customers) {
          keeperLookupRef.current.set(customer.id, customer);
        }
        const options = result.customers
          .filter((customer) => customer.id !== duplicate.id)
          .map(customerToOption);
        setKeeperOptions(options);
      } catch {
        // silently ignore — dropdown just shows empty
      } finally {
        setKeeperSearchLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keeperSearchInput, duplicate.id]);

  const handleKeeperChange = (value: number | string) => {
    const id = Number(value);
    setSelectedKeeperId(id);
    setConfirmed(false);
    setModalError(null);
    setFieldErrors({});
    setSelectedKeeper(id > 0 ? keeperLookupRef.current.get(id) ?? null : null);
  };

  const canSubmit =
    selectedKeeper != null &&
    selectedKeeper.id !== duplicate.id &&
    selectedKeeper.active &&
    confirmed &&
    !submitting;

  const handleSubmit = async () => {
    if (!selectedKeeper || !canSubmit) return;

    setModalError(null);
    setFieldErrors({});

    try {
      setSubmitting(true);
      const keeper = await mergeCustomers(duplicate.id, selectedKeeper.id);
      onMerged(keeper);
      onClose();
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setModalError(err.message);
      } else {
        setModalError(err instanceof Error ? err.message : 'Failed to merge customers');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <GitMerge className="h-6 w-6 text-red-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Merge Customer</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Combine a duplicate customer into another. This cannot be undone.
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {modalError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{modalError}</span>
            </div>
          )}

          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Duplicate (will be deactivated)
            </p>
            <p className="mt-1 text-sm font-semibold text-gray-900">{duplicate.display_name}</p>
            <p className="text-xs text-gray-500">
              {[duplicate.email, duplicate.phone].filter(Boolean).join(' · ') || 'No contact details'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Merge into <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              options={keeperOptions}
              value={selectedKeeperId}
              onChange={handleKeeperChange}
              placeholder="Select the customer to keep"
              searchPlaceholder="Search active customers..."
              emptyMessage="No active customers found"
              onSearchChange={setKeeperSearchInput}
              isLoading={keeperSearchLoading}
            />
            {fieldError('into_customer_id') && (
              <p className="mt-1 text-sm text-red-600">{fieldError('into_customer_id')}</p>
            )}
          </div>

          {selectedKeeper && (
            <>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Field</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Duplicate</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Keeper</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr>
                      <td className="px-3 py-2 text-gray-500">Display name</td>
                      <td className="px-3 py-2 text-gray-900">{duplicate.display_name}</td>
                      <td className="px-3 py-2 text-gray-900">{selectedKeeper.display_name}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-gray-500">Email</td>
                      <td className="px-3 py-2 text-gray-900">{duplicate.email || '—'}</td>
                      <td className="px-3 py-2 text-gray-900">{selectedKeeper.email || '—'}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-gray-500">Phone</td>
                      <td className="px-3 py-2 text-gray-900">{duplicate.phone || '—'}</td>
                      <td className="px-3 py-2 text-gray-900">{selectedKeeper.phone || '—'}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-gray-500">Address</td>
                      <td className="px-3 py-2 text-gray-900">{duplicate.address || '—'}</td>
                      <td className="px-3 py-2 text-gray-900">{selectedKeeper.address || '—'}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-gray-500">Type / Terms</td>
                      <td className="px-3 py-2 text-gray-900">
                        {formatLabel(duplicate.customer_type)} / {formatLabel(duplicate.payment_terms)}
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        {formatLabel(selectedKeeper.customer_type)} / {formatLabel(selectedKeeper.payment_terms)}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-gray-500">Open balance</td>
                      <td className="px-3 py-2 text-gray-900">{formatCurrency(balanceOf(duplicate))}</td>
                      <td className="px-3 py-2 text-gray-900">{formatCurrency(balanceOf(selectedKeeper))}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-gray-500">Credit balance</td>
                      <td className="px-3 py-2 text-gray-900">{formatCurrency(creditOf(duplicate))}</td>
                      <td className="px-3 py-2 text-gray-900">{formatCurrency(creditOf(selectedKeeper))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 space-y-1.5">
                <p className="font-medium">Before you merge:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>This is permanent and cannot be undone.</li>
                  <li>The duplicate is deactivated, not deleted — its history is kept.</li>
                  <li>
                    The keeper&apos;s profile fields (name, email, phone, address, type, terms) stay
                    unchanged. Edit the keeper first if you need to copy anything over.
                  </li>
                  <li>Bill-to details already printed on existing invoices/quotations are left as-is.</li>
                  <li>
                    The duplicate&apos;s unapplied credit ({formatCurrency(creditOf(duplicate))}) moves to
                    the keeper but is not auto-applied to the keeper&apos;s open invoices.
                  </li>
                </ul>
              </div>

              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="h-4 w-4 mt-0.5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <span>
                  I understand this merge is permanent and{' '}
                  <strong>{duplicate.display_name}</strong> cannot be recovered as a separate customer.
                </span>
              </label>
            </>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            className="px-4 py-2 rounded-lg text-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Merging…' : 'Merge customers'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerMergeModal;
